/* screens/academy/landing/CourseLandingPage.jsx — маршрут /course/:slug (#16).
   ОДНА ссылка, двойное поведение (модель владельца):
   - Авторизован (useAuth().user) → лендинг ВНУТРИ сервиса с полной оболочкой
     <AppShell> (сайдбар/профиль/вкладки/моб. таб-бар). URL тот же.
   - Аноним → standalone (только страница курса, без оболочки); CTA → /signup?next=…
   Маршрут объявлен на верхнем уровне (НЕ под RequireAuth) — оболочку выбирает этот
   компонент по факту авторизации.

   Данные — getPublicLanding(slug) (AllowAny; с сессией бэк добавит my_enrollment).
   404 (нет курса / не опубликован — единый ответ, S12 анти-энумерация) → дружелюбный
   экран «Курс не найден». */

import React from 'react';
import { AppShell } from '../../../app/layouts/AppShell.jsx';
import { useAuth } from '../../../app/providers/AuthProvider.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { Icon } from '../../../components/ui/Icon.jsx';
import { Spinner } from '../../../components/ui/Spinner.jsx';
import { hooks } from '../../../hooks/index.jsx';
import { RR } from '../../../lib/router.jsx';
import * as api from '../../../lib/api/academy.js';
import { CourseLanding } from './CourseLanding.jsx';

const { useFakeQuery } = hooks;
const { useParams, useNavigate } = RR;

/* AD-17: SEO/OG страницы продаж. `meta` приходит с бэка ({title, description,
   og_image} — уже с фолбэками на курс) и проставляется в <head> НА КЛИЕНТЕ.
   ГРАНИЦА, которую важно не переврать: это SPA. Мессенджеры и поисковые роботы
   JavaScript не исполняют — они прочтут index.html, а не то, что мы дописали
   после загрузки. Значит здесь работает вкладка браузера и разметка для тех, кто
   JS выполняет; полноценное превью в чате требует серверной отдачи метатегов
   ботам (см. отчёт AD-17). Теги ставим через DOM-API и текстом — никакого
   innerHTML: title/description приходят из БД и остаются ДАННЫМИ. */
function applyLandingMeta(meta) {
  if (typeof document === 'undefined' || !meta) return undefined;
  const title = (meta.title || '').trim();
  const description = (meta.description || '').trim();
  const image = (meta.og_image || '').trim();
  const prevTitle = document.title;
  const added = [];   // теги, которых в head не было — их же и уберём
  if (title) document.title = title + ' — Ресториум';
  const put = (selector, attr, key, value) => {
    if (!value) return;
    let el = document.head.querySelector(selector);
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute(attr, key);
      document.head.appendChild(el);
      added.push(el);
    }
    el.setAttribute('content', value);
  };
  put('meta[name="description"]', 'name', 'description', description);
  put('meta[property="og:title"]', 'property', 'og:title', title);
  put('meta[property="og:description"]', 'property', 'og:description', description);
  put('meta[property="og:image"]', 'property', 'og:image', image);
  put('meta[property="og:type"]', 'property', 'og:type', 'website');
  put('meta[name="twitter:card"]', 'name', 'twitter:card',
    image ? 'summary_large_image' : 'summary');
  // Уход со страницы возвращает всё как было: иначе после перехода в «Кухню»
  // вкладка продолжала бы называться курсом, а чужие OG-теги висели бы на всём
  // приложении. Управления заголовком вкладки в приложении больше нигде нет —
  // убирать за собой обязано то место, которое его меняет.
  return () => {
    document.title = prevTitle;
    added.forEach((el) => el.remove());
  };
}

// Состояние загрузки — нейтральный standalone-экран (одинаков под/вне оболочки).
function LandingLoading() {
  return (
    <div className="cl-state">
      <Spinner size="lg" />
      <div className="t-label">Загружаем курс…</div>
    </div>
  );
}

// 404 / ошибка — дружелюбный экран. 404 = «Курс не найден» (не палим существование).
function LandingError({ status, navigate }) {
  const notFound = status === 404;
  return (
    <div className="cl-state">
      <div className="cl-state__glyph"><Icon name={notFound ? 'book' : 'alert-circle'} size={24} /></div>
      <h2 className="t-h2">{notFound ? 'Курс не найден' : 'Не удалось открыть курс'}</h2>
      <p className="text-dim">
        {notFound
          ? 'Возможно, ссылка устарела или курс ещё не опубликован.'
          : 'Попробуйте обновить страницу чуть позже.'}
      </p>
      <Button variant="primary" onClick={() => navigate('/learning')}>В Академию</Button>
    </div>
  );
}

function CourseLandingPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const q = useFakeQuery('public-landing-' + slug, () => api.getPublicLanding(slug), [slug]);

  // AD-17: заголовок вкладки и OG-разметка — из payload.meta, как только он пришёл.
  React.useEffect(() => {
    if (!q.data || !q.data.meta) return undefined;
    return applyLandingMeta(q.data.meta);
  }, [q.data]);

  let inner;
  if (q.loading && !q.data) inner = <LandingLoading />;
  else if (q.error || !q.data) inner = <LandingError status={q.error && q.error.status} navigate={navigate} />;
  else inner = <CourseLanding payload={q.data} mode={user ? 'app' : 'anon'} />;

  // Авторизован → внутри AppShell (полная оболочка), URL тот же /course/:slug.
  // Анониму → standalone (без сайдбара/таб-бара/профиля).
  if (user) {
    return (
      <AppShell>
        <div className="section-page">
          <div className="section-body">{inner}</div>
        </div>
      </AppShell>
    );
  }

  // standalone: своя минимальная шапка-бренд (как на странице сертификата), без чрома.
  return (
    <div className="cl-standalone">
      <header className="cl-topbar">
        <span className="cl-topbar__brand">
          <img src="/logo-mark.svg" alt="" width={22} height={22} />Ресториум
        </span>
        <span className="cl-topbar__spacer" />
        <a className="btn btn--secondary btn--sm" href={`/signin?next=${encodeURIComponent('/course/' + slug)}`}>
          Войти
        </a>
      </header>
      <main className="cl-standalone__main">{inner}</main>
    </div>
  );
}

export { CourseLandingPage };
